import { useEffect, useState } from "react";
import { PenLine, Save, Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiErrorMessage } from "@/lib/api";

const emptyRow = () => ({ nama: "", jabatan: "" });

export function ReportSignatureKonfigSection() {
  const [lokasi, setLokasi] = useState("Jakarta");
  const [disiapkan, setDisiapkan] = useState([emptyRow()]);
  const [disetujui, setDisetujui] = useState([emptyRow()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/report-signature");
      setLokasi(data.lokasi || "Jakarta");
      setDisiapkan(data.disiapkan?.length ? data.disiapkan : [emptyRow()]);
      setDisetujui(data.disetujui?.length ? data.disetujui : [emptyRow()]);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const updRow = (list, setList, idx, key, val) =>
    setList(list.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const addRow = (list, setList) => setList([...list, emptyRow()]);
  const delRow = (list, setList, idx) =>
    setList(list.length > 1 ? list.filter((_, i) => i !== idx) : [emptyRow()]);

  async function save() {
    setSaving(true);
    try {
      const clean = (arr) =>
        arr
          .map((r) => ({ nama: (r.nama || "").trim(), jabatan: (r.jabatan || "").trim() }))
          .filter((r) => r.nama || r.jabatan);
      await api.put("/settings/report-signature", {
        lokasi: (lokasi || "").trim() || "Jakarta",
        disiapkan: clean(disiapkan),
        disetujui: clean(disetujui),
      });
      toast.success("Pengaturan tanda tangan laporan disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const renderGroup = (title, hint, list, setList, prefix) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800">{title}</h3>
          <p className="text-[11px] text-slate-500">{hint}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1 h-8 text-xs"
          onClick={() => addRow(list, setList)}
          data-testid={`add-${prefix}`}
        >
          <Plus size={13} /> Tambah
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {list.map((row, idx) => (
          <div key={idx} className="flex items-start gap-2" data-testid={`row-${prefix}-${idx}`}>
            <Input
              value={row.nama}
              onChange={(e) => updRow(list, setList, idx, "nama", e.target.value)}
              placeholder="Nama Pejabat"
              className="flex-1"
              data-testid={`input-${prefix}-nama-${idx}`}
            />
            <Input
              value={row.jabatan}
              onChange={(e) => updRow(list, setList, idx, "jabatan", e.target.value)}
              placeholder="Jabatan"
              className="flex-1"
              data-testid={`input-${prefix}-jabatan-${idx}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0 h-9 w-9"
              onClick={() => delRow(list, setList, idx)}
              data-testid={`del-${prefix}-${idx}`}
            >
              <Trash2 size={15} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="section-report-signature">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
          <PenLine size={18} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">
            Pengaturan Tanda Tangan Laporan
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Blok tanda tangan otomatis dibubuhkan di bagian bawah <b>semua Export Excel</b>
            {" "}(Rekap Gaji, Rekap Gaji Kustom, dan Rekap Pembebanan). Ubah lokasi, nama, dan
            jabatan pejabat di sini kapan saja.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 text-center text-slate-500 py-6 text-sm">Memuat pengaturan…</div>
      ) : (
        <>
          <div className="mt-5">
            <Label className="text-xs font-bold text-slate-600">Lokasi (Kota)</Label>
            <Input
              value={lokasi}
              onChange={(e) => setLokasi(e.target.value)}
              placeholder="Jakarta"
              className="mt-1.5 max-w-xs"
              data-testid="input-ttd-lokasi"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Tampil sebagai <b>"{lokasi || "Jakarta"}, [Tanggal Unduh]"</b> di atas blok tanda tangan.
            </p>
          </div>

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {renderGroup(
              "Disiapkan oleh,",
              "Pejabat penyusun (sisi kiri).",
              disiapkan, setDisiapkan, "disiapkan",
            )}
            {renderGroup(
              "Disetujui dan diketahui oleh,",
              "Pejabat pemberi persetujuan (sisi kanan).",
              disetujui, setDisetujui, "disetujui",
            )}
          </div>

          <div className="mt-4 rounded-md border border-teal-100 bg-teal-50/60 p-3 flex items-start gap-2 text-[11px] text-teal-900">
            <Info size={13} className="mt-0.5 shrink-0" />
            <div>
              Nama pejabat dicetak <b>Tebal &amp; Bergaris Bawah</b>, jabatan dicetak <i>miring</i> di
              bawahnya. Tanggal diambil otomatis dari tanggal unduh file.
            </div>
          </div>

          <div className="mt-4">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-teal-700 hover:bg-teal-800 gap-1.5"
              data-testid="save-report-signature"
            >
              <Save size={13} /> {saving ? "…" : "Simpan"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default ReportSignatureKonfigSection;
